<template>
  <div class="rbt" :class="[`rbt--${urgency}`, {'rbt--selected': selected, 'rbt--compact': compact, 'rbt--quiet': isQuiet}]"
       role="button" tabindex="0" @click="$emit('select', room)">
    <!-- attention rail: colored ONLY when something needs a human -->
    <span class="rbt__rail"/>

    <div class="rbt__top">
      <span class="rbt__num tabular" :class="{'rbt__num--long': isLongLabel}"
            v-b-tooltip.hover :title="numTitle">{{ room.roomNumber }}</span>
      <span class="rbt__clean" :class="`rbt__clean--${cleanTone}`"
            v-b-tooltip.hover :title="$t(`roomBoard.clean.${room.cleaning}`)"/>
      <span class="rbt__spacer"/>
      <span class="rbt__state" :class="`rbt__state--${stateTone}`">{{ $t(`roomBoard.occ.${room.occupancy}`) }}</span>
    </div>

    <div class="rbt__mid">
      <template v-if="room.occupancy === 'TURNOVER'">
        <div class="rbt__guest">
          <span class="rbt__guest-out">{{ shortName(room.departing && room.departing.guestName) }}</span>
          <i class="fas fa-arrow-right-long rbt__guest-arrow"/>
          <span>{{ shortName(room.reservation && room.reservation.guestName) }}</span>
        </div>
        <div v-if="!compact" class="rbt__meta tabular">
          {{ $t('roomBoard.until') }} {{ (room.departing && room.departing.checkOutTime) || '11:00' }}
          · {{ $t('roomBoard.checkIn') }} {{ (room.reservation && room.reservation.checkInTime) || '15:00' }}
        </div>
      </template>

      <template v-else-if="room.reservation">
        <div class="rbt__guest">{{ room.reservation.guestName || '—' }}</div>
        <div v-if="!compact" class="rbt__meta tabular">
          <span v-if="room.reservation.guests">{{ room.reservation.guests }} · </span>{{ room.reservation.checkIn | moment('DD MMM') }} → {{ room.reservation.checkOut | moment('DD MMM') }}<span v-if="room.reservation.nights"> · {{ room.reservation.nights }}{{ $t('roomBoard.nightsShort') }}</span>
        </div>
      </template>

      <template v-else>
        <div class="rbt__guest rbt__guest--none">—</div>
        <div v-if="!compact" class="rbt__meta tabular">
          <template v-if="room.nextArrival">{{ $t('roomBoard.nextArrival') }} {{ room.nextArrival.date | moment('DD MMM') }}</template>
          <template v-else>&nbsp;</template>
        </div>
      </template>
    </div>

    <div class="rbt__foot">
      <span v-if="footFlag" class="rbt__flag" :class="`rbt__flag--${urgency}`">{{ footFlag }}</span>
      <span class="rbt__spacer"/>
      <span v-if="damageCount" class="rbt__count rbt__count--damage" v-b-tooltip.hover :title="damageTooltip">
        <i class="fas fa-screwdriver-wrench"/><template v-if="damageCount > 1"> {{ damageCount }}</template>
      </span>
      <span v-if="room.reservation && room.reservation.dayRate" class="rbt__money tabular">{{ room.reservation.dayRate | numberFormatter }} €</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RoomBoardCard',
  props: {
    room: {type: Object, required: true},
    selected: {type: Boolean, default: false},
    compact: {type: Boolean, default: false},
  },
  computed: {
    /** attention drives the visual weight: normal → warn → urgent */
    urgency() {
      const attention = this.room.attention || []
      if (attention.includes('DIRTY_ARRIVAL') || attention.includes('OCCUPIED_DAMAGE')) return 'urgent'
      if (attention.length) return 'warn'
      return 'normal'
    },
    /** clean + available recedes: near-monochrome so busy rooms pop */
    isQuiet() {
      return this.room.occupancy === 'AVAILABLE' && this.room.cleaning === 'CLEAN'
    },
    /** labels that are names, not numbers, shrink + ellipsize; the tooltip carries the rest */
    isLongLabel() {
      return String(this.room.roomNumber || '').length > 8
    },
    numTitle() {
      return this.isLongLabel ? (this.room.fullAddress || this.room.roomName || '') : ''
    },
    stateTone() {
      return {OCCUPIED: 'blue', ARRIVAL: 'blue', DEPARTURE: 'amber', TURNOVER: 'violet', AVAILABLE: 'mute', MAINTENANCE: 'gray'}[this.room.occupancy] || 'mute'
    },
    cleanTone() {
      return {CLEAN: 'ok', DIRTY: 'bad', IN_PROGRESS: 'work', OVERDUE: 'bad'}[this.room.cleaning] || 'ok'
    },
    /** ONE short operational line — the thing to do or know, not a pile of pills */
    footFlag() {
      const arrivalTime = this.room.reservation && this.room.reservation.checkInTime
      if (this.room.cleaning === 'DIRTY' || this.room.cleaning === 'OVERDUE') {
        if (this.room.occupancy === 'ARRIVAL' || this.room.occupancy === 'TURNOVER') {
          return `${this.$t('roomBoard.clean.DIRTY')} · ${this.$t('roomBoard.checkIn')} ${arrivalTime || ''}`.trim()
        }
        return this.$t(`roomBoard.clean.${this.room.cleaning}`)
      }
      if (this.room.cleaning === 'IN_PROGRESS') return this.$t('roomBoard.clean.IN_PROGRESS')
      if (this.room.occupancy === 'ARRIVAL' && arrivalTime) return `${this.$t('roomBoard.checkIn')} ${arrivalTime}`
      if (this.room.occupancy === 'DEPARTURE') {
        const out = this.room.reservation && this.room.reservation.checkOutTime
        return out ? `${this.$t('roomBoard.checkOut')} ${out}` : null
      }
      return null
    },
    damageCount() {
      return Array.isArray(this.room.damages) ? this.room.damages.length : 0
    },
    damageTooltip() {
      return (this.room.damages || []).map(d => d.description || '').filter(Boolean).join(' · ') || this.$t('roomBoard.maintenance')
    },
  },
  methods: {
    shortName(name) {
      if (!name) return '—'
      const parts = name.trim().split(/\s+/)
      return parts.length > 1 ? `${parts[0][0]}. ${parts[parts.length - 1]}` : parts[0]
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/*
 * Ledger tile: flat surface, hairline border, 8px radius, NO shadow at rest.
 * Color appears only where it means something — the attention rail, the
 * cleanliness dot, the state word. Everything else is typography.
 */
.rbt {
  position: relative;
  display: flex; flex-direction: column;
  background: #fff;
  border: 1px solid rgba(23, 30, 48, .1);
  border-radius: 8px;
  padding: 8px 11px 8px 13px;
  cursor: pointer;
  min-width: 0;
  transition: border-color .12s ease, box-shadow .12s ease, background .12s ease;

  &:hover { border-color: rgba(23, 30, 48, .28); box-shadow: 0 3px 12px -6px rgba(23, 30, 48, .25); }
  &:focus { outline: none; }
  &--selected { border-color: #3b6ff6; box-shadow: 0 0 0 1px #3b6ff6; }

  &__rail {
    position: absolute; left: -1px; top: 6px; bottom: 6px; width: 3px;
    border-radius: 0 2px 2px 0; background: transparent;
  }
  &--warn .rbt__rail { background: #e8a23d; }
  &--urgent .rbt__rail { background: #e05252; }
  &--warn { background: #fffdf7; }
  &--urgent { background: #fff9f8; }

  /* quiet rooms recede */
  &--quiet {
    background: #fbfcfd;
    .rbt__num { color: #7c8598; font-weight: 700; }
    .rbt__guest--none { color: #c3c9d4; }
  }

  &__top { display: flex; align-items: baseline; gap: 7px; }
  &__num {
    font-size: 1.02rem; font-weight: 800; color: #171e30; letter-spacing: -.015em; line-height: 1.1;
    min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    &--long { font-size: .8rem; letter-spacing: 0; align-self: center; }
  }
  &__clean {
    width: 7px; height: 7px; border-radius: 50%; align-self: center; flex-shrink: 0; cursor: help;
    &--ok { background: #2fa46a; }
    &--bad { background: #e05252; }
    &--work { background: #e8a23d; }
  }
  &__spacer { flex: 1; }
  &__state {
    font-size: .58rem; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; white-space: nowrap;
    &--blue { color: #3b6ff6; }
    &--amber { color: #c07f14; }
    &--violet { color: #8a5cf6; }
    &--gray { color: #8a93a5; }
    &--mute { color: #b3bac6; font-weight: 700; }
  }

  &__mid { margin-top: 4px; min-height: 30px; }
  &__guest {
    display: flex; align-items: center; gap: 5px;
    font-size: .8rem; font-weight: 600; color: #2a3348;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &--none { color: #b3bac6; font-weight: 500; }
  }
  &__guest-out { color: #9aa2b1; font-weight: 500; }
  &__guest-arrow { font-size: .56rem; color: #b3bac6; flex-shrink: 0; }
  &__meta { font-size: .66rem; color: #9aa2b1; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  &__foot { display: flex; align-items: center; gap: 7px; margin-top: 6px; min-height: 15px; }
  &__flag {
    font-size: .6rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    color: #8a93a5; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &--warn { color: #c07f14; }
    &--urgent { color: #cc3f3f; font-weight: 800; }
  }
  &__count {
    font-size: .62rem; font-weight: 700; flex-shrink: 0;
    &--damage { color: #cc3f3f; }
  }
  &__money { font-size: .72rem; font-weight: 700; color: #4a5468; flex-shrink: 0; }

  /* ledger line for compact mode */
  &--compact {
    flex-direction: row; align-items: center; gap: 12px; padding: 5px 11px 5px 13px; border-radius: 6px;
    .rbt__top { flex: 0 0 132px; min-width: 0; }
    .rbt__mid { margin: 0; min-height: 0; flex: 1; min-width: 0; display: flex; align-items: baseline; gap: 10px; }
    .rbt__meta { margin: 0; }
    .rbt__foot { margin: 0; flex: 0 0 auto; min-width: 0; }
    .rbt__flag { max-width: 200px; }
    .rbt__num { font-size: .88rem; }
  }
}
</style>

<style lang="scss">
.dark-layout {
  .rbt {
    background: #232b42; border-color: rgba(255, 255, 255, .09);
    &:hover { border-color: rgba(255, 255, 255, .25); box-shadow: 0 3px 12px -6px rgba(0, 0, 0, .5); }
    &--selected { border-color: #5b8bff; box-shadow: 0 0 0 1px #5b8bff; }
    &--warn { background: #2a2d3e; }
    &--urgent { background: #2e2738; }
    &--quiet { background: #1f2639; }
  }
  .rbt__num { color: #eef1f7; }
  .rbt--quiet .rbt__num { color: #737d92; }
  .rbt__guest { color: #cfd5e1; }
  .rbt__money { color: #aeb6c6; }
  .rbt__state--blue { color: #7ba3ff; }
  .rbt__state--violet { color: #b393ff; }
  .rbt__state--amber { color: #e0a94e; }
}
</style>
